import {
  Box,
  DialogContent,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StandardDialog from "../../components/StandardDialog";
import { useThemeContext } from "../../context/themeContext";
import BadgeLevels from "./BadgeLevels";
import ChecklistSteps from "./ChecklistSteps";

/**
 * How a row progresses, from its DTO's `details`, as an animated ladder:
 * levels for a badge (BadgeLevels), steps for a checklist (ChecklistSteps).
 * Both animate on every open, since the popover/dialog remounts its content.
 *
 * A popover on desktop; the app's standard dialog on touch devices, where a
 * popover anchored to a small row is cramped.
 */
export default function AchievementDetails({
  row,
  anchorEl,
  open,
  onClose,
  onAction,
  onSkip,
}) {
  const { isTouchDevice } = useThemeContext();
  if (!row.details) return null;

  const content = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 0.5 }}>
        <Typography variant="body1" fontWeight="bold">
          {row.title}
        </Typography>
        {/* Badges show their level from 0; checklists have none */}
        {row.kind === "badge" && (
          <Typography variant="body2" color="text.secondary">
            Level {row.level}
          </Typography>
        )}
        {/* Mobile dialog only: tapping outside isn't obvious to everyone, and
            Android's back button doesn't close dialogs here. A 44px target
            pulled into the padding so it doesn't grow the header. */}
        {isTouchDevice && (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              ml: "auto",
              alignSelf: "center",
              width: 44,
              height: 44,
              my: -1.5,
              mr: -1.5,
              color: "text.secondary",
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {/* How it works, plus a maxed badge's count (the ladder shows it otherwise) */}
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {row.details.description}
        {row.kind === "badge" && row.isMaxed && ` · ${row.details.summary}`}
      </Typography>

      {row.kind === "badge" ? (
        <BadgeLevels row={row} />
      ) : (
        <ChecklistSteps row={row} onAction={onAction} onSkip={onSkip} />
      )}
    </Box>
  );

  if (isTouchDevice) {
    return (
      <StandardDialog open={open} onClose={onClose} maxWidth="xs">
        <DialogContent sx={{ p: 0 }}>{content}</DialogContent>
      </StandardDialog>
    );
  }
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ mt: 1 }}
      slotProps={{ paper: { sx: { width: 300 } } }}
    >
      {content}
    </Popover>
  );
}
